// Route Code Edited 7/17/2026
/**
 * route-checkout-subtotal-update-slidecart.js
 *
 * Updates Route's cloned checkout button on the slide-cart drawer to show
 * the Route package protection quote (e.g. "Checkout with package protection
 * +$6.75") instead of just the cart subtotal. Adapted from
 * route-checkout-subtotal-update.js for this theme's drawer markup, which
 * nests the price several levels deep and has no direct text-node prefix on
 * the button itself.
 *
 * Currency formatting matches Route's own widget (shopify-widget/src/app/
 * widget/utils/formatters.ts formatCurrency): Intl.NumberFormat('en-US',
 * {style:'currency', currency}) with the store's actual ISO currency code —
 * not the theme's own money_format. Mimicking the theme's format was tried
 * first and caused visible inconsistency: for a store using Albanian Lek,
 * the theme's format shows "Lek 400.00" while Route's own widget header
 * (right next to the button) shows "ALL 400.00" for the same amount — two
 * different displays of the same number on the same page. Matching Route's
 * own algorithm keeps this script's output consistent with the widget it's
 * decorating.
 *
 * Known limitation: ROUTE_PRICE_SELECTOR (#pw-coverage-description) is not
 * guaranteed present on every Route widget variant — confirm it exists on
 * this merchant's configured variant before relying on this script.
 *
 * Dependencies:
 *   - Route installed (detected via #route-widget-observer-root).
 *   - window.Shopify.currency.active for the store's ISO currency code
 *     (standard Shopify global, independent of theme formatMoney). Falls
 *     back to reading a currency code directly out of Route's own price
 *     element text if that global isn't present.
 *
 * Installation:
 *   <script src="{{ 'route-checkout-subtotal-update-slidecart.js' | asset_url }}" defer></script>
 */
(function () {
  // Scoped to this drawer's button class specifically, so this script never
  // touches an unrelated Route clone elsewhere on the page (e.g. a separate
  // /cart page checkout button).
  const CLONED_BUTTON_SELECTOR   = '[data-route-cloned-button="true"].xb_slide_cart_button_checkout';
  const ROUTE_PRICE_SELECTOR     = '#pw-coverage-description';
  const ROUTE_ROOT_ID            = 'route-widget-observer-root';
  const PRICE_CORRECTED_ATTR     = 'data-route-price-corrected';
  const SEPARATOR_CHAR           = '+';
  const SEPARATOR_CLASS          = 'route-price-separator'; // for CSS targeting/spacing
  const PRICE_FOUND_EVENT        = 'route-price-element-found';
  const UPDATE_TITLE_ATTRIBUTE   = true; // set false to leave the title attribute untouched

  // Matches this theme's checkout button class; excludes Route's own clone
  // (clonePrimaryButton preserves class/type on the clone, so type/name
  // alone can't distinguish original from clone here).
  const ORIGINAL_BUTTON_SELECTOR = 'button.xb_slide_cart_button_checkout:not([data-route-cloned-button])';

  const instanceState = new WeakMap();
  // { originalBtn, prefix, originalTitle, guardObserver,
  //   lastKnownGood, lastSetAriaLabel, intercepted }

  let outerObserver = null;
  let routePriceObserver = null;
  let pendingScan = false;
  let priceElementSeen = false;

  // ─── Locale-agnostic numeric token handling ─────────────────────────────────

  function extractNumericToken(text) {
    const match = text.match(/\d[\d.,]*\d|\d/);
    return match ? match[0] : null;
  }

  function parseNumericToken(token) {
    if (!token) return null;
    const sepMatches = [...token.matchAll(/[.,]/g)];
    if (!sepMatches.length) {
      const value = parseInt(token, 10);
      return isNaN(value) ? null : value;
    }
    const lastSep = sepMatches[sepMatches.length - 1];
    const decimalSep = token[lastSep.index];
    const groupSep = decimalSep === '.' ? ',' : '.';
    const normalized = token.split(groupSep).join('').replace(decimalSep, '.');
    const value = parseFloat(normalized);
    return isNaN(value) ? null : value;
  }

  // ─── Formatting ────────────────────────────────────────────────────────────

  // Finds where a symbol/word prefix directly before a numeric token starts,
  // handling both glued ("$6.75") and space-separated ("Lek 6.75") currency
  // formats. A plain whitespace-boundary scan only catches the glued case;
  // for space-separated formats it stops at the very first character (the
  // space) and returns nothing, silently dropping the currency prefix.
  function findPricePrefixStart(str, tokenIndex) {
    let start = tokenIndex;
    while (start > 0 && !/\s/.test(str[start - 1])) start--;
    if (start === tokenIndex && start > 0 && /\s/.test(str[start - 1])) {
      let wordStart = start - 1;
      while (wordStart > 0 && !/\s/.test(str[wordStart - 1])) wordStart--;
      if (wordStart < start - 1) start = wordStart;
    }
    return start;
  }

  // Finds the price element regardless of nesting depth: walks span/b/strong/em
  // descendants and returns whichever one owns a direct text-node child with
  // a numeric token (this theme nests the price span > span > span).
  function findPriceElement(root) {
    if (!root) return null;
    const candidates = root.querySelectorAll('span, b, strong, em');
    for (let i = candidates.length - 1; i >= 0; i--) {
      const el = candidates[i];
      for (const node of el.childNodes) {
        if (node.nodeType === Node.TEXT_NODE && extractNumericToken(node.textContent)) {
          return el;
        }
      }
    }
    return null;
  }

  // Store's real ISO currency code (e.g. "ALL", "EUR") — standard Shopify
  // global, present on virtually every storefront regardless of whether the
  // theme defines formatMoney.
  function getStoreCurrencyCode() {
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return null;
  }

  // Mirrors Route's own formatCurrency (shopify-widget/src/app/widget/utils/
  // formatters.ts) exactly: Intl.NumberFormat('en-US', {style:'currency',
  // currency}). For currencies without a dedicated en-US symbol mapping,
  // this prints the ISO code as the prefix (e.g. "ALL 400.00") — same as
  // Route's own widget header, which is the point: matching output, not
  // reinventing formatting.
  function formatCurrency(value, currencyCode) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  // Reads Route's own quote value and, where possible, the currency it's
  // displayed in — preferring the Shopify global over sniffing Route's
  // rendered text (which only contains a distinguishable ISO code when
  // Intl falls back to one; USD/EUR/GBP etc. render as symbols instead).
  function getRoutePriceInfo() {
    const el = document.querySelector(ROUTE_PRICE_SELECTOR);
    if (!el) return null;
    const text = el.textContent;
    const token = extractNumericToken(text);
    if (!token) return null;
    const value = parseNumericToken(token);
    if (value === null) return null;

    let currencyCode = getStoreCurrencyCode();
    if (!currencyCode) {
      const tokenIndex = text.indexOf(token);
      const start = findPricePrefixStart(text, tokenIndex);
      const prefix = text.slice(start, tokenIndex).trim();
      if (/^[A-Z]{2,4}$/.test(prefix)) currencyCode = prefix;
    }
    return { value, currencyCode };
  }

  // Prefix comes from the paired original button's text (cached once at
  // pairing time), not the clone's — there's no direct text-node child of
  // the clone to read ("Checkout • " sits inside a nested span here).
  function getButtonPrefix(originalBtn) {
    if (!originalBtn) return '';
    const fullText = originalBtn.textContent || '';
    const token = extractNumericToken(fullText);
    if (!token) return fullText.replace(/\s+/g, ' ').trim();
    const tokenIndex = fullText.indexOf(token);
    const start = findPricePrefixStart(fullText, tokenIndex);
    const prefix = fullText.slice(0, start);
    return prefix.replace(/[•·\-:|]+\s*$/, '').replace(/\s+/g, ' ').trim();
  }

  // Patches the title attribute (lives on a nested span, not the button
  // itself). Always regenerates from state.originalTitle — a pristine copy
  // captured once from the original button — rather than the live attribute,
  // so repeated price changes can't stack "+" onto a previous write. Replaces
  // the entire old prefix+number chunk (e.g. "Lek 23,400.00") with
  // "+" + the Route-formatted string (e.g. "+ALL 400.00"), rather than trying
  // to preserve the original prefix — the whole point is to show Route's own
  // formatting here, not the theme's.
  function updateTitleAttribute(clonedBtn, state, withCurrency) {
    if (!UPDATE_TITLE_ATTRIBUTE) return;
    const titleEl = clonedBtn.querySelector('[title]');
    if (!titleEl) return;
    const baseTitle = state.originalTitle;
    if (!baseTitle) return;
    const token = extractNumericToken(baseTitle);
    if (!token) return;

    const tokenIndex = baseTitle.indexOf(token);
    const start = findPricePrefixStart(baseTitle, tokenIndex);
    const newTitle = baseTitle.slice(0, start) + SEPARATOR_CHAR + withCurrency + baseTitle.slice(tokenIndex + token.length);
    if (newTitle !== titleEl.getAttribute('title')) {
      titleEl.setAttribute('title', newTitle);
    }
  }

  // ─── Write functions ─────────────────────────────────────────────────────────

  function writePriceToSpan(spanEl, withCurrency) {
    for (let i = spanEl.childNodes.length - 1; i >= 0; i--) {
      const node = spanEl.childNodes[i];
      if (node.nodeType === Node.TEXT_NODE && extractNumericToken(node.textContent)) {
        node.textContent = withCurrency;
        return;
      }
    }
    spanEl.appendChild(document.createTextNode(withCurrency));
  }

  // Leading "+" (e.g. "+$6.75") rather than trailing ("$6.75+", which reads
  // as "or more"). Matches on SEPARATOR_CLASS specifically and enforces
  // position as well as content, so a stale separator left over from an
  // earlier run gets moved to the front rather than left in place.
  function writeSeparatorToSpan(spanEl) {
    for (let i = 0; i < spanEl.childNodes.length; i++) {
      const node = spanEl.childNodes[i];
      if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains(SEPARATOR_CLASS)) {
        if (node.textContent.trim() !== SEPARATOR_CHAR) {
          node.textContent = SEPARATOR_CHAR;
        }
        if (spanEl.firstChild !== node) {
          spanEl.insertBefore(node, spanEl.firstChild);
        }
        return;
      }
    }
    const b = document.createElement('b');
    b.textContent = SEPARATOR_CHAR;
    b.className = SEPARATOR_CLASS;
    spanEl.insertBefore(b, spanEl.firstChild);
  }

  // ─── Pairing ─────────────────────────────────────────────────────────────────

  function findPairedOriginal(clonedBtn) {
    let node = clonedBtn.parentElement;
    while (node) {
      const matches = node.querySelectorAll(ORIGINAL_BUTTON_SELECTOR);
      if (matches.length >= 1) return matches[0];
      node = node.parentElement;
    }
    return null;
  }

  // ─── Guard (per instance) ───────────────────────────────────────────────────

  function attachGuard(clonedBtn) {
    const state = instanceState.get(clonedBtn);
    if (!state) return;
    if (state.guardObserver) state.guardObserver.disconnect();

    state.guardObserver = new MutationObserver(() => {
      if (state.lastKnownGood === null) return;
      const writeTarget = findPriceElement(clonedBtn);
      if (!writeTarget) return;
      writeSeparatorToSpan(writeTarget);
      if (writeTarget.textContent.trim() !== state.lastKnownGood.trim()) {
        writePriceToSpan(writeTarget, state.lastKnownGood);
      }
    });

    state.guardObserver.observe(clonedBtn, { subtree: true, childList: true });
  }

  // ─── Reconcile (per instance) ───────────────────────────────────────────────

  function reconcile(clonedBtn) {
    const state = instanceState.get(clonedBtn);
    if (!state) return;
    const writeTarget = findPriceElement(clonedBtn);
    if (!writeTarget) return;

    const info = getRoutePriceInfo();
    if (!info) return;
    const withCurrency = formatCurrency(info.value, info.currencyCode);

    const separatorNeedsWrite = (() => {
      for (let i = 0; i < writeTarget.childNodes.length; i++) {
        const node = writeTarget.childNodes[i];
        if (node.nodeType === Node.ELEMENT_NODE) return node.textContent.trim() !== SEPARATOR_CHAR;
      }
      return true;
    })();

    if (withCurrency === state.lastKnownGood && !separatorNeedsWrite) return;

    if (state.guardObserver) state.guardObserver.disconnect();
    writeSeparatorToSpan(writeTarget);
    writePriceToSpan(writeTarget, withCurrency);
    state.lastKnownGood = withCurrency;
    clonedBtn.setAttribute(PRICE_CORRECTED_ATTR, '');
    attachGuard(clonedBtn);
    updateTitleAttribute(clonedBtn, state, withCurrency);

    // Only overwrite aria-label if it's empty or matches what this script
    // last wrote — Route may set its own (e.g. from a merchant-configured
    // custom CTA text), and clobbering that would mismatch the visible text.
    const currentAriaLabel = clonedBtn.getAttribute('aria-label');
    const newAriaLabel = state.prefix ? `${state.prefix} ${withCurrency}` : withCurrency;
    if (!currentAriaLabel || currentAriaLabel === state.lastSetAriaLabel) {
      clonedBtn.setAttribute('aria-label', newAriaLabel);
      state.lastSetAriaLabel = newAriaLabel;
    }
  }

  function reconcileAll() {
    document.querySelectorAll(CLONED_BUTTON_SELECTOR).forEach((btn) => {
      if (instanceState.has(btn)) reconcile(btn);
    });
  }

  // ─── innerHTML interception (per instance) ─────────────────────────────────

  function interceptInnerHTML(clonedBtn) {
    const state = instanceState.get(clonedBtn);
    if (!state || state.intercepted) return;
    state.intercepted = true;

    const nativeDescriptor = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');
    Object.defineProperty(clonedBtn, 'innerHTML', {
      set(value) {
        nativeDescriptor.set.call(this, value);
        reconcile(clonedBtn);
      },
      get() {
        return nativeDescriptor.get.call(this);
      },
      configurable: true,
      enumerable: true,
    });
  }

  // Note: this script used to also watch the original button's subtree to
  // catch subtotal changes, back when the injected price mimicked the
  // subtotal's own format. Now that pricing is derived entirely from
  // Route's own price element (via getRoutePriceInfo/formatCurrency, see
  // file header), the original button's content no longer affects
  // formatting, so that observer was removed as dead weight.

  // ─── Shared Route price watcher ─────────────────────────────────────────────

  function watchRoutePrice() {
    const routePriceEl = document.querySelector(ROUTE_PRICE_SELECTOR);
    if (!routePriceEl) return;
    if (routePriceObserver) routePriceObserver.disconnect();

    routePriceObserver = new MutationObserver(() => reconcileAll());
    routePriceObserver.observe(routePriceEl, { characterData: true, childList: true, subtree: true });
  }

  // ─── Price element arrival detection ────────────────────────────────────────

  function checkMutationsForPriceElement(mutationList) {
    if (priceElementSeen) return;
    for (const mutation of mutationList) {
      for (const addedNode of mutation.addedNodes) {
        if (addedNode.nodeType !== Node.ELEMENT_NODE) continue;
        if (addedNode.matches && addedNode.matches(ROUTE_PRICE_SELECTOR)) {
          priceElementSeen = true;
          document.dispatchEvent(new CustomEvent(PRICE_FOUND_EVENT));
          return;
        }
        if (addedNode.querySelector && addedNode.querySelector(ROUTE_PRICE_SELECTOR)) {
          priceElementSeen = true;
          document.dispatchEvent(new CustomEvent(PRICE_FOUND_EVENT));
          return;
        }
      }
    }
  }

  // outerObserver's job is to notice a NEW cloned button appearing (drawer
  // reopened, Route re-cloned after a cart update) — not routine childList
  // churn on elements already tracked. Only a mutation whose addedNodes
  // contain (or contain a descendant matching) a new cloned button warrants
  // a rescan.
  function mutationsContainNewClonedButton(mutationList) {
    for (const mutation of mutationList) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.matches && node.matches(CLONED_BUTTON_SELECTOR)) return true;
        if (node.querySelector && node.querySelector(CLONED_BUTTON_SELECTOR)) return true;
      }
    }
    return false;
  }

  document.addEventListener(PRICE_FOUND_EVENT, () => {
    watchRoutePrice();
    reconcileAll();
  });

  // ─── Cloned button setup ────────────────────────────────────────────────────

  function processClonedButton(btn) {
    if (instanceState.has(btn)) return;

    const originalBtn = findPairedOriginal(btn);
    const prefix = getButtonPrefix(originalBtn);
    // Title captured from originalBtn, not the clone — this script never
    // writes to originalBtn, so it's a reliable pristine source even if the
    // drawer reuses the same clone DOM node across sessions.
    const titleSourceEl = originalBtn || btn;
    const titleEl = titleSourceEl.querySelector('[title]');
    const originalTitle = titleEl ? titleEl.getAttribute('title') : null;

    instanceState.set(btn, {
      originalBtn,
      prefix,
      originalTitle,
      guardObserver: null,
      lastKnownGood: null,
      lastSetAriaLabel: null,
      intercepted: false,
    });

    interceptInnerHTML(btn);
    attachGuard(btn);
    reconcile(btn);
  }

  function scanForClonedButtons() {
    document.querySelectorAll(CLONED_BUTTON_SELECTOR).forEach((btn) => {
      if (!instanceState.has(btn)) {
        processClonedButton(btn);
      } else {
        reconcile(btn);
      }
    });
    watchRoutePrice();
  }

  // ─── Outer observer ──────────────────────────────────────────────────────────

  function attachObserver() {
    if (outerObserver) outerObserver.disconnect();
    if (document.querySelector(ROUTE_PRICE_SELECTOR)) priceElementSeen = true;

    outerObserver = new MutationObserver((mutationList) => {
      checkMutationsForPriceElement(mutationList);

      if (!mutationsContainNewClonedButton(mutationList)) return;

      if (pendingScan) return;
      pendingScan = true;
      requestAnimationFrame(() => {
        pendingScan = false;
        scanForClonedButtons();
      });
    });

    outerObserver.observe(document.body, { childList: true, subtree: true });
    scanForClonedButtons();
  }

  // ─── Init ───────────────────────────────────────────────────────────────────

  function waitForRoot() {
    if (document.getElementById(ROUTE_ROOT_ID)) {
      attachObserver();
      return;
    }

    let initTimeout = null;
    const bodyObserver = new MutationObserver(() => {
      if (!document.getElementById(ROUTE_ROOT_ID)) return;
      bodyObserver.disconnect();
      clearTimeout(initTimeout);
      attachObserver();
    });

    bodyObserver.observe(document.body, { childList: true, subtree: true });
    initTimeout = setTimeout(() => bodyObserver.disconnect(), 10000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForRoot);
  } else {
    waitForRoot();
  }

  // ─── bfcache ────────────────────────────────────────────────────────────────

  window.addEventListener('pagehide', () => {
    if (outerObserver) outerObserver.disconnect();
    if (routePriceObserver) routePriceObserver.disconnect();
    document.querySelectorAll(CLONED_BUTTON_SELECTOR).forEach((btn) => {
      const state = instanceState.get(btn);
      if (state) {
        if (state.guardObserver) state.guardObserver.disconnect();
      }
    });
  });

  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    priceElementSeen = false;
    document.querySelectorAll(CLONED_BUTTON_SELECTOR).forEach((btn) => {
      instanceState.delete(btn);
    });
    attachObserver();
  });
})();